import { create } from "zustand";
import { useNewGroupChatState } from "./NewGroupChatState";

import {
  getChatFromLocalDB,
  // updateChatInLocalDB,
} from "@/indexedDB/services/chats";
import { ChatType } from "@/server/types/chat";
import { InitChatStatus } from "@/indexedDB/types";
import { MemberRole } from "@/server/types/member";
import { persist } from "zustand/middleware";
import { useConnectionState } from "./ConnectionState";
import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
import { SocketEventTypes } from "@/socketIO/types";
import { checkIfChatMessagesAreReallySynced } from "@/utils/eventsSyncFix";
import { useRtfInputState } from "./RtfInputState";
import { featureFlags } from "@/config/featureFlags";

// TO DO => extract in different states by logic (active chat state vs nav state)
// TO DO => investigate use of live query subscriptions from zustand via idexed db -  avoid manual updates

// Define the MenuType enum
export enum ActiveNavMenu {
  P2P_CHATS = 1,
  GROUP_CHATS = 2,
  NOTIFICATIONS = 3,
  SETTINGS = 4,
  AI_CHAT = 5,
}

// Define the ActionType enum
export enum ChatUserAction {
  CREATE_P2P_CHAT = 1,
  CREATE_GROUP_CHAT = 2,
  CREATE_AI_CHAT = 4,
  SEARCH_AI_CHAT = 5,
  SEARCH_P2P_CHAT = 6,
  SEARCH_GROUP_CHAT = 7,
  VIEW_SUBSCRIPTION = 8,
  VIEW_ADDON_CREDITS = 9,
  VIEW_INVOICES = 10,
  VIEW_PAYMENTS_FAQ = 11,
  // Append-only: values persist to localStorage ("nav-state") as raw numbers.
  VIEW_SETTINGS_CATEGORY = 12,
}

/**
 * Settings categories rendered as middle-column views (flag
 * `settingsMiddleViews`). The menu order is this list's order; deep links
 * (/settings/:category) validate against it.
 */
export type SettingsCategory =
  | "profile"
  | "security"
  | "appearance"
  | "language"
  | "preferences";

export const IMPLEMENTED_SETTINGS_CATEGORIES: readonly SettingsCategory[] = [
  "profile",
  "security",
  "appearance",
  "language",
  "preferences",
];

export function isPaymentsAction(action: ChatUserAction | null): boolean {
  return (
    action === ChatUserAction.VIEW_SUBSCRIPTION ||
    action === ChatUserAction.VIEW_ADDON_CREDITS ||
    action === ChatUserAction.VIEW_INVOICES ||
    action === ChatUserAction.VIEW_PAYMENTS_FAQ
  );
}

/**
 * Every action that renders a full middle-column view (payments panes + the
 * flag-gated settings category view). Consumers: useChatRouteSync's reset
 * guard, MiddleColumn's empty-state/trigger, ChatListEntry's stale-URL
 * recovery.
 *
 * The settings term is FLAG-GATED on purpose: `nav-state` persists raw action
 * numbers and zustand skips `migrate` when versions match, so a
 * VIEW_SETTINGS_CATEGORY persisted by a flagged build survives rehydration in
 * a later flag-off build. Gating here makes that orphan a non-pane: route sync
 * resets it and the legacy UI renders — never the new views.
 */
export function isMiddlePaneAction(action: ChatUserAction | null): boolean {
  return (
    isPaymentsAction(action) ||
    (featureFlags.settingsMiddleViews &&
      action === ChatUserAction.VIEW_SETTINGS_CATEGORY)
  );
}

type State = {
  // Persisted state
  activeChatId: string | null;
  activeThreadId: string | null;
  activeChatHubId: string | null;

  // Non-persisted state
  activeChatECSK: string;
  activeChatName: string;
  activeChatDescription?: string;
  activeChatAllowThreads: boolean;
  activeChatCanPost: MemberRole;
  activeChatIsPublic: boolean;
  activeChatType: ChatType;
  isActiveChatInitialMessagesLoaded: boolean;
  isActiveChatFullMessageListLoaded: boolean;
  activeChatInitStatus: InitChatStatus;
  activeChatMemberCount: number;
  activeChatMuted: boolean;
  activeChatRoleAsMember: MemberRole;
  activeChatRemovedRoleAsMember: boolean; // used for when current user is removed from a chat

  activeChatAvatar: { attachmentId?: string; secondMemberId?: string } | null;
  showChatDetails: boolean;

  showThreadList: boolean;

  activeNavMenu: ActiveNavMenu;
  activeChatUserAction: ChatUserAction | null;
  /** Which settings category the middle-column view shows (action 12 only). */
  activeSettingsCategory: SettingsCategory | null;

  // temporary
  activeChatBatchCount?: number; // batches of messages loaded via progressive loading
  activeChatRenderedBatchCount?: number; // batches of messages rendered in the UI via infinite scroll
  activeChatMessageTake: number;
};

type Actions = {
  setActiveChatId: (chatId: string | null) => void;
  setActiveThreadId: (threadId: string | null) => void;

  startAIChatCreation: () => void;
  cancelAIChatCreation: () => void;
  startSubscriptionView: () => void;
  cancelSubscriptionView: () => void;
  startAddonCreditsView: () => void;
  startInvoicesView: () => void;
  startPaymentsFaqView: () => void;
  cancelPaymentsView: () => void;
  startSettingsCategoryView: (category: SettingsCategory) => void;
  cancelSettingsCategoryView: () => void;
  setActiveChatMuted: (val: boolean) => void;
  setActiveChatMemberCount: (count: number) => void;
  setActiveChatRoleAsMember: (role: number) => void;
  setActiveChatRemovedRoleAsMember: (val: boolean) => void;
  setActiveChatName: (name: string) => void;
  setActiveChatIsPublic: (isPublic: boolean) => void;
  setActiveChatDescription: (description: string) => void;
  setActiveChatAllowThreads: (allowThreads: boolean) => void;
  setActiveChatCanPost: (canPost: MemberRole) => void;
  setActiveChatAvatar: (
    avatar: { attachmentId?: string; secondMemberId?: string } | null,
  ) => void;

  setActiveChatInitialMessagesLoaded: (val: boolean) => void;
  setActiveChatFullMessageListLoaded: (val: boolean) => void;
  setActiveChatInitStatus: (val: InitChatStatus) => void;
  toggleChatDetails: () => void;
  setActiveNavMenu: (menuType: ActiveNavMenu) => void;
  setChatUserAction: (actionType: ChatUserAction | null) => void;

  // temp
  setActiveChatBatchCount: (count: number) => void;
  setActiveChatRenderedBatchCount: (count: number) => void;
  setActiveChatMessageTake: (count: number) => void;

  resetNavigationState: () => void;
};

export const initialState: State = {
  activeChatId: null,
  activeChatHubId: null,
  activeThreadId: null,

  activeChatECSK: "",
  activeChatName: "",
  activeChatDescription: "",
  activeChatIsPublic: false,
  activeChatAllowThreads: false,
  activeChatCanPost: MemberRole.MEMBER,
  activeChatType: ChatType.P2P,
  isActiveChatInitialMessagesLoaded: false,
  isActiveChatFullMessageListLoaded: false,
  activeChatInitStatus: InitChatStatus.PENDING,
  activeChatMemberCount: 0,
  activeChatMuted: false,
  activeChatRoleAsMember: MemberRole.MEMBER,
  activeChatRemovedRoleAsMember: false, // used for when current user is removed from a chat
  activeChatAvatar: null,

  activeNavMenu: ActiveNavMenu.AI_CHAT,
  showChatDetails: false,
  activeChatUserAction: ChatUserAction.CREATE_AI_CHAT,
  activeSettingsCategory: null,
  showThreadList: false,

  // temporary
  activeChatBatchCount: 0, // batches of messages loaded via progressive loading
  activeChatRenderedBatchCount: 0, // batches of messages rendered in the UI via infinite scroll
  activeChatMessageTake: MESSAGE_LIST_PAGE_SIZE,
};

export const useNavigationState = create<State & Actions>()(
  persist(
    (set) => ({
      // State
      ...initialState,

      // Actions
      setActiveChatId: async (chatId: string | null) => {
        if (!chatId) {
          // TO DO = WORKAROUND - reset active chat id and active chat type
          // get activemenuType from the store
          const activeMenuType = useNavigationState.getState().activeNavMenu;

          set(initialState);
          set({ activeNavMenu: activeMenuType }); // reset active chat id but keep the active menu type
        } else {
          // check if chat is in local db
          const chatDetails = await getChatFromLocalDB(chatId || "");

          if (!chatDetails) {
            return;
          }

          // Gated: new sync engine handles event reconciliation
          if (!featureFlags.useNewSync) {
            const socket = useConnectionState.getState().socket;

            if (chatDetails.fullMsgListInit) {
              socket?.emit(SocketEventTypes.FETCH_EVENTS, {
                timestamp: chatDetails.lastSyncedAt,
                chat_id: chatId,
              });
            }
            // TO DO => investigate if this is needed after fixing events sync logic
            // is this async/await needed here?
            checkIfChatMessagesAreReallySynced(chatDetails);
          }

          set({
            activeChatId: chatId,
            activeChatHubId: chatDetails.hubId,
            activeChatECSK: chatDetails.eCSK,
            activeChatName: chatDetails.name,
            activeChatDescription: chatDetails.description,
            activeChatIsPublic: chatDetails.isPublic,
            activeChatAllowThreads: chatDetails.allowThreads,
            activeChatCanPost: chatDetails.canPost,
            activeChatType: chatDetails.type,
            isActiveChatInitialMessagesLoaded:
              chatDetails.firstPhaseMsgListInit,
            isActiveChatFullMessageListLoaded: chatDetails.fullMsgListInit,
            activeChatInitStatus: chatDetails.initStatus,
            activeChatMemberCount: chatDetails.memberCount,
            activeChatMuted: chatDetails.isMuted,
            activeChatRoleAsMember: chatDetails.roleAsMember.role,
            activeChatRemovedRoleAsMember: chatDetails.roleAsMember.removed
              ? true
              : false,
            activeChatAvatar: {
              attachmentId: chatDetails.avatar?.attachmentId,
              secondMemberId: chatDetails.avatar?.secondMemberId,
            },

            showChatDetails: false,
            activeChatUserAction: null,
            activeSettingsCategory: null,
            activeThreadId: null,
            showThreadList: false,

            // temp
            activeChatBatchCount: chatDetails.batchCount,
            activeChatRenderedBatchCount: chatDetails.renderedBatchCount,
            activeChatMessageTake: MESSAGE_LIST_PAGE_SIZE,
            // activeChatLoadingMessages: chatDetails.isLoadingMessages
            //   ? chatDetails.isLoadingMessages
            //   : false,
            // activeChatHasMoreMessages: chatDetails.hasMoreMessages
            //   ? chatDetails.hasMoreMessages
            //   : false,
          });

          // if (chatDetails.fullMsgListInit) {
          //   await updateChatInLocalDB(chatDetails.id, {
          //     lastSyncedAt: new Date().toISOString(),
          //   });
          // }
        }
      },

      setActiveThreadId: (threadId: string | null) => {
        const resetRtfInputState =
          useRtfInputState.getState().resetRtfInputState;

        set({ activeThreadId: threadId });
        set({ showChatDetails: false });
        set({ showThreadList: false });

        resetRtfInputState();
      },
      startAIChatCreation: () => {
        set({
          activeChatId: null,
          activeThreadId: null,
          activeChatUserAction: ChatUserAction.CREATE_AI_CHAT,
          showChatDetails: false,
          showThreadList: false,
        });
      },

      cancelAIChatCreation: () => {
        set({
          activeChatUserAction: null,
          showChatDetails: false,
          showThreadList: false,
        });
      },

      startSubscriptionView: () => {
        set({
          activeChatId: null,
          activeThreadId: null,
          activeChatUserAction: ChatUserAction.VIEW_SUBSCRIPTION,
          showChatDetails: false,
          showThreadList: false,
        });
      },

      cancelSubscriptionView: () => {
        set({ activeChatUserAction: null });
      },

      startAddonCreditsView: () => {
        set({
          activeChatId: null,
          activeThreadId: null,
          activeChatUserAction: ChatUserAction.VIEW_ADDON_CREDITS,
          showChatDetails: false,
          showThreadList: false,
        });
      },

      startInvoicesView: () => {
        set({
          activeChatId: null,
          activeThreadId: null,
          activeChatUserAction: ChatUserAction.VIEW_INVOICES,
          showChatDetails: false,
          showThreadList: false,
        });
      },

      startPaymentsFaqView: () => {
        set({
          activeChatId: null,
          activeThreadId: null,
          activeChatUserAction: ChatUserAction.VIEW_PAYMENTS_FAQ,
          showChatDetails: false,
          showThreadList: false,
        });
      },

      cancelPaymentsView: () => {
        set({ activeChatUserAction: null });
      },

      startSettingsCategoryView: (category: SettingsCategory) => {
        set({
          activeChatId: null,
          activeThreadId: null,
          activeChatUserAction: ChatUserAction.VIEW_SETTINGS_CATEGORY,
          activeSettingsCategory: category,
          showChatDetails: false,
          showThreadList: false,
        });
      },

      cancelSettingsCategoryView: () => {
        set({ activeChatUserAction: null, activeSettingsCategory: null });
      },

      setActiveChatMuted: (val: boolean) => set({ activeChatMuted: val }),

      setActiveChatName: (name: string) => set({ activeChatName: name }),

      setActiveChatDescription: (description: string) =>
        set({ activeChatDescription: description }),

      setActiveChatAllowThreads: (allowThreads: boolean) =>
        set({ activeChatAllowThreads: allowThreads }),

      setActiveChatCanPost: (canPost: MemberRole) =>
        set({ activeChatCanPost: canPost }),

      setActiveChatAvatar: (
        avatar: { attachmentId?: string; secondMemberId?: string } | null,
      ) => set({ activeChatAvatar: avatar }),

      setActiveChatIsPublic: (isPublic: boolean) =>
        set({ activeChatIsPublic: isPublic }),

      setActiveChatMemberCount: (val: number) =>
        set({ activeChatMemberCount: val }),

      setActiveChatRoleAsMember: (val: number) =>
        set({ activeChatRoleAsMember: val }),

      setActiveChatRemovedRoleAsMember: (val: boolean) =>
        set({ activeChatRemovedRoleAsMember: val }),

      setActiveChatInitialMessagesLoaded: (val: boolean) =>
        set({ isActiveChatInitialMessagesLoaded: val }),

      setActiveChatFullMessageListLoaded: (val: boolean) =>
        set({ isActiveChatFullMessageListLoaded: val }),

      setActiveChatInitStatus: (val: InitChatStatus) =>
        set({ activeChatInitStatus: val }),

      toggleChatDetails: () =>
        set((state) => ({ showChatDetails: !state.showChatDetails })),

      setActiveNavMenu: (menuType: ActiveNavMenu) => {
        // TO DO -  get the activeMenuType from the store
        // TO DO - and check if the value is the same as the new value

        // Check if the provided menuType is a valid value of the ActiveMenuType enum
        if (!Object.values(ActiveNavMenu).includes(menuType)) {
          return; // Do nothing if the value is invalid
        }

        set((state) => {
          // prevent setting the same menu type
          if (menuType === state.activeNavMenu) {
            return state;
          }
          // reset useNewGroupChatState state
          // TO DO => improve this by adding a reset method to the store to prevent reseting every time the menu changes
          useNewGroupChatState.getState().resetNewGroupChatState();
          return {
            activeNavMenu: menuType,
            activeChatUserAction: null,
            activeSettingsCategory: null,
          };
        });
      },

      setChatUserAction: (actionType: ChatUserAction | null) => {
        set({ activeChatUserAction: actionType });
      },

      // temp
      setActiveChatBatchCount: (count: number) =>
        set({ activeChatBatchCount: count }),
      setActiveChatRenderedBatchCount: (count: number) =>
        set({ activeChatRenderedBatchCount: count }),
      setActiveChatMessageTake(count) {
        set({ activeChatMessageTake: count });
      },

      resetNavigationState: () => {
        set(initialState);
      },
    }),
    {
      name: "nav-state",
      version: 5,
      // Sanitize the persisted action/category PAIR at hydration. The two are
      // only ever written atomically at runtime, so an inconsistent pair can
      // only enter through storage (tampering, partial write, a category
      // removed in a later build). Without this, a flag-ON build hydrating
      // action 12 with an invalid category renders a full-screen middle-column
      // overlay whose shell shows nothing — no back control, unrecoverable on
      // mobile.
      merge: (persisted, current) => {
        const merged = {
          ...current,
          ...(persisted as Partial<State>),
        };
        if (
          merged.activeChatUserAction ===
            ChatUserAction.VIEW_SETTINGS_CATEGORY &&
          (!merged.activeSettingsCategory ||
            !IMPLEMENTED_SETTINGS_CATEGORIES.includes(
              merged.activeSettingsCategory,
            ))
        ) {
          merged.activeChatUserAction = null;
          merged.activeSettingsCategory = null;
        }
        return merged;
      },
      migrate: (persisted: unknown, fromVersion: number) => {
        const state = (persisted ?? {}) as Partial<State & Actions> & {
          activeNetworkId?: unknown;
        };
        // Strip fields removed from the State type so they don't re-persist.
        delete state.activeNetworkId;
        if (fromVersion < 5) {
          state.activeNavMenu = ActiveNavMenu.AI_CHAT;
        }
        return state;
      },
      // Used for partially persiting states
      // partialize: (state) => ({
      //   activeChatId: state.activeChatId,
      // }),
    },
  ),
);
